import { createRouter, createWebHistory } from 'vue-router'
import Home from './pages/Home.vue'
import Fishing from './pages/Fishing.vue'
import Shop from './pages/Shop.vue'
import Inventory from './pages/Inventory.vue'
import Maps from './pages/Maps.vue'
import Login from './pages/Login.vue'
import Error404 from './pages/Error404.vue'
import BuyConfirm from './pages/BuyConfirm.vue'
import HouseRest from './pages/HouseRest.vue'
import ResetPassword from './pages/ResetPassword.vue'
import ConfirmEmail from './pages/ConfirmEmail.vue'
import Roulette from './pages/Roulette.vue'
import Upgrade from './pages/Upgrade.vue'
import { useGtag } from 'vue-gtag-next'
import ClanPage from '@/pages/Clan.vue';
import { restaurarSessao, temSessao } from '@/core/services/api/v2';

const routes = [
  {
    path: '/refer/:identifier',
    name: 'Referral',
    meta: {
      title: 'Fishbay - Bem-vindo ao mundo dos pescadores',
      allowUnauthenticated: true
    },
    beforeEnter: (to, from, next) => {
      // Save referral ID to sessionStorage
      if (to.params.identifier) {
        sessionStorage.setItem('referralId', to.params.identifier);
      }
      // Redirect to login page
      next({ name: 'Login' });
    }
  },
  {
    path: '/',
    name: 'Login',
    component: Login,
    meta: {
      title: 'Fishbay - Bem-vindo ao mundo dos pescadores',
      description: 'Entre no Fishbay e comece sua jornada no mundo da pesca. Capture peixes, complete missões e torne-se um mestre pescador!'
    }
  },
  {
    path: '/home',
    name: 'Home',
    component: Home,
    meta: {
      title: 'Fishbay - Bem-vindo ao mundo dos pescadores',
      description: 'Explore o mundo da pesca no Fishbay. Descubra novos lugares, complete missões e melhore suas habilidades de pesca.'
    }
  },
  {
    path: '/fishing',
    name: 'Fishing',
    component: Fishing,
    meta: {
      title: 'Fishbay - Pesque os mais incríveis peixes',
      description: 'Pesque uma grande variedade de peixes raros e únicos. Use diferentes iscas e equipamentos para maximizar suas chances!'
    }
  },
  {
    path: '/shop',
    name: 'Shop',
    component: Shop,
    meta: {
      title: 'Fishbay - Compre os melhores itens e venda os melhores peixes',
      description: 'Compre equipamentos de pesca, iscas especiais e venda seus peixes por moedas. Melhore seu equipamento para pescar peixes mais raros!'
    }
  },
  {
    path: '/inventory',
    name: 'Inventory',
    component: Inventory,
    meta: {
      title: 'Fishbay - Tudo sobre seu pescador',
      description: 'Gerencie seu inventário, veja seus equipamentos, peixes capturados e conquistas desbloqueadas.'
    }
  },
  {
    path: '/maps',
    name: 'Maps',
    component: Maps,
    meta: {
      title: 'Fishbay - Viaje pelo mundo da pesca',
      description: 'Explore diferentes locais de pesca, cada um com seus peixes únicos e desafios especiais.'
    }
  },
  {
    path: '/payment/success',
    name: 'PaymentSuccess',
    component: BuyConfirm,
    meta: {
      title: 'Fishbay - Compra confirmada'
    }
  },
  {
    path: '/payment/cancel',
    redirect: '/home',
    meta: {
      title: 'Fishbay - Compra cancelada'
    }
  },
  {
    path: '/house-rest',
    name: 'HouseRest',
    component: HouseRest,
    meta: {
      title: 'Fishbay - Descanse para pegar os melhores peixes'
    }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    props: (route) => ({ code: route.query.code }),
    meta: {
      title: 'Fishbay - Redefinir Senha',
      allowUnauthenticated: true
    }
  },
  {
    /**
     * O destino do link de confirmacao que o e-mail (Resend) manda. Sem
     * autenticacao: quem clica pode estar em outro navegador, e o codigo do
     * link e a prova — nao a sessao.
     */
    path: '/confirm-email',
    name: 'EmailConfirmation',
    component: ConfirmEmail,
    meta: {
      title: 'Fishbay - Confirmar E-mail',
      allowUnauthenticated: true
    }
  },
  {
    path: '/roulette',
    name: 'Roulette',
    component: Roulette,
    meta: {
      title: 'Fishbay - Roleta da Sorte',
      description: 'Gire a roleta e ganhe recompensas incríveis! Use seus tickets para tentar a sorte.'
    }
  },
  {
    path: '/upgrade',
    name: 'Upgrade',
    component: Upgrade,
    meta: {
      title: 'Fishbay - Upgrades de Pescador',
      description: 'Melhore as habilidades do seu pescador para pegar peixes maiores e mais valiosos!'
    }
  },
  {
    path: '/business',
    name: 'Business',
    component: () => import('./pages/Business.vue'),
    meta: {
      title: 'Fishbay - Área de Negócios',
      description: 'Gerencie seus negócios, contrate funcionários e aumente seus lucros na área de negócios do Fishbay.'
    }
  },
  {
    path: '/clan',
    name: 'Clan',
    component: ClanPage,
    meta: {
      requiresAuth: true
    }
  },
  /**
   * A 1.2 — o segundo loop do jogo, paralelo à pesca.
   *
   * `import()` dinâmico como o de Negócios: são cinco seções e um modal que
   * ninguém que só pesca precisa baixar.
   */
  {
    path: '/exploration',
    name: 'Exploration',
    component: () => import('./pages/Exploration.vue'),
    meta: {
      requiresAuth: true,
      title: 'Fishbay - Expedições',
      description: 'Recrute integrantes, monte squads e mande expedições explorar os biomas do mundo de Fishbay.'
    }
  },
  /**
   * O mercado entre jogadores.
   *
   * `import()` dinâmico como a exploração e Negócios: são três abas, um
   * formulário e dois modais que ninguém que só pesca precisa baixar.
   */
  {
    path: '/market',
    name: 'Market',
    component: () => import('./pages/Mercado.vue'),
    meta: {
      requiresAuth: true,
      title: 'Fishbay - Mercado',
      description: 'Compre e venda com outros pescadores: integrantes de squad, trabalhadores, materiais e itens de exploração, por ouro ou Parallelium.'
    }
  },
  {
    path: '/profile/:username',
    name: 'Profile',
    component: () => import('./pages/Profile.vue'),
    meta: {
      title: 'Fishbay - Perfil do Jogador',
      description: 'Veja as estatísticas, conquistas e equipamentos de um pescador do Fishbay.'
    }
  },
  /**
   * 2.12 — o catálogo dos primitivos, só em desenvolvimento.
   *
   * O D18 diz que os primitivos vêm antes das telas. Esta rota é como se verifica que eles estão
   * realmente prontos: aparecem fora de qualquer tela, lado a lado, onde uma divergência entre dois
   * é visível antes de virar duas telas diferentes.
   *
   * Fora de produção não é segredo, é peso: doze componentes e três cenários que nenhum jogador
   * precisa baixar. O `import()` dinâmico garante que ele nem entre no bundle quando a rota não é
   * registrada.
   *
   * `allowUnauthenticated` porque conferir um botão não deveria exigir uma sessão.
   */
  ...(process.env.NODE_ENV === 'production'
    ? []
    : [
        {
          path: '/dev/primitivos',
          name: 'Primitivos',
          component: () => import('./pages/Primitivos.vue'),
          meta: {
            title: 'Fishbay - Primitivos',
            allowUnauthenticated: true
          }
        }
      ]),
  {
    path: '/:pathMatch(.*)*',
    name: 'Error404',
    component: Error404,
    meta: {
      title: 'Fishbay - Página não encontrada'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title || 'Fishbay';
  
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    document.head.appendChild(metaDescription);
  }
  metaDescription.content = to.meta.description || 'Fishbay - O melhor jogo de pesca online';

  const isLoginPage = to.name === 'Login';
  const allowUnauthenticated = to.meta.allowUnauthenticated;
  /**
   * Espera a restauração da sessão antes de decidir.
   *
   * Antes era `!!Cookies.get('jwt')`, lido na hora. No V2 o token que dá
   * sessão longa é o refresh, num cookie httpOnly invisível para este código:
   * quem tem sessão válida só descobre depois de uma ida ao servidor. Ler um
   * cookie e decidir na mesma linha mandava para o login quem estava logado.
   *
   * `restaurarSessao` é memoizada — este await custa uma requisição no
   * primeiro roteamento e zero nos seguintes.
   */
  await restaurarSessao();
  const temSessaoAtiva = temSessao();
  const isCreatingPlayer = from.name === 'Login' && to.name === 'Login';

  // Se tem sessão e não está criando player, redireciona para Home
  if (temSessaoAtiva && isLoginPage && !isCreatingPlayer) {
    next({ name: 'Home' });
    return;
  }
  
  // Se não tem sessão e a rota exige autenticação
  if (!temSessaoAtiva && !isLoginPage && !allowUnauthenticated) {
    next({ name: 'Login' });
    return;
  }

  next();

  // Track page view
  useGtag().pageview({
    page_path: to.fullPath,
    page_title: to.name,
    page_location: window.location.href
  });
});

export default router
